import { Injectable, inject, WritableSignal, Signal, isSignal, signal } from '@angular/core';
import { of, Observable, firstValueFrom } from 'rxjs';
import { take, tap, catchError } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UserService } from '../services/user.service';
import { ProductService } from 'src/app/services/product.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { KebnekaiseService } from "src/app/services/kebnekaise.service";
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { SecurityContext } from '@angular/core';

type Id = string | number;
interface Org { id: Id; }
interface UserLike { id: Id; items_per_page?: number | string | null; }

@Injectable({ providedIn: 'root' })
export class UserHelperService {
  // ✅ injicera här (fält), inte i en metod
  private userService = inject(UserService);
  private productService = inject(ProductService);
  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);
  private organisationService = inject(OrganisationService);
  private kebnekaiseService = inject(KebnekaiseService);

  // Internal cache for departments
  private listAllDepartments = signal<any[]>([]);
  /**
   * Gets items-per-page setting for a user/org.
   * - Hämtar från backend via UserService
   * - Faller tillbaka på localStorage vid behov
   * - Skriver in i en WritableSignal<number> (itemsPerPage)
   */
  getItemsPerPage(
    currentUser: Signal<UserLike | null> | UserLike | null,
    organisation: Org | null,
    itemsPerPage: WritableSignal<number>
  ): void {
    const userId = isSignal(currentUser) ? currentUser()?.id : currentUser?.id;
    const orgId  = organisation?.id;

    // Skicka payload (anpassa till din service – om den kräver sträng, använd JSON.stringify)
    const info = JSON.stringify({ userId, orgId });
    this.userService.getProductListSettings(info);

    const parseNum = (v: unknown): number | null => {
      if (v == null) return null;
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    };

    this.userService.getCurrentUserDataListener()
      .pipe(take(1))
      .subscribe({
        next: (user: UserLike | null | undefined) => {
          const lsVal = localStorage.getItem('items_per_page');

          // 1) Ingen user -> prova localStorage
          if (!user) {
            const n = parseNum(lsVal);
            if (n !== null) itemsPerPage.set(n);
            return;
          }

          if (user.items_per_page == null || user.items_per_page === undefined) {
            localStorage.setItem('items_per_page', String(10));
            itemsPerPage.set(10);
            return;
          }

          // 2) User har värde och ingen lokal override -> använd user + spara i LS
          if (user.items_per_page != null) {
            const n = parseNum(user.items_per_page);
            if (n !== null) {
              itemsPerPage.set(n);
              localStorage.setItem('items_per_page', String(n));
            }
            return;
          }

          // 3) Lokal override finns -> använd den om giltig
          if (lsVal !== null) {
            const n = parseNum(lsVal);
            if (n !== null) itemsPerPage.set(n);
          }
        },
        error: () => {
          // no-op: behåll nuvarande itemsPerPage vid fel
        }
      });
  }

  getProductId(product: { doc_product_id?: any; id?: any; product_id?: any; sareq_id?: any; sds_file_id?: any }) {
    return product?.doc_product_id ?? product?.sareq_id ?? product?.id ?? product?.product_id;
  }

  openGrbInNewWindow(product: { id: string | number; name: string }): void {
    // (Removed unused filename var to avoid TS noUnusedLocals errors)
    const tmpid = this.getProductId(product);
    this.productService.getGrbAsTrustedResourceUrl(tmpid);

    this.productService.getGrbDataUpdatedListener()
      .pipe(take(1))
      .subscribe({
        next: (pdfContent: string | SafeResourceUrl | null) => {
          if (!pdfContent) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_GRB'),
              this.translate.instant('COULD_NOT_GET_GRB_WARNING'),
              'error'
            );
            return;
          }

          // Ensure we have a plain string URL
          const url =
            typeof pdfContent === 'string'
              ? pdfContent
              : this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, pdfContent) ?? null;

          if (!url) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_GRB'),
              this.translate.instant('COULD_NOT_GET_GRB_WARNING'),
              'error'
            );
            return;
          }

          const newWindow = window.open(url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Fallback if popup blocked
            window.location.href = url;
          }
        },
        error: () => {
          Swal.fire(
            this.translate.instant('COULD_NOT_GET_GRB'),
            this.translate.instant('COULD_NOT_GET_GRB_WARNING'),
            'error'
          );
        }
      });
  }

  openKebnekaiseSdsInNewWindow(product: {
    sds_file_id?: string;
    kebnekaise_id?: string;
    Kebnekaise_id?: string;
  }): void {
    // Kept to preserve original behavior, though it's not used downstream
    const filename = `${product?.sds_file_id ?? ''}.pdf`;

    const kebId = product?.kebnekaise_id ?? product?.Kebnekaise_id;
    if (!kebId) {
      Swal.fire(
        this.translate.instant('COULD_NOT_GET_SDS'),
        this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
        'error'
      );
      return;
    }

    this.kebnekaiseService.getSdsAsTrustedResourceUrl(kebId);

    this.kebnekaiseService
      .getKebnekaiseDataListener()
      .pipe(take(1))
      .subscribe({
        next: (pdfContent: string | null) => {
          if (!pdfContent) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_SDS'),
              this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
              'error'
            );
            return;
          }

          const newWindow = window.open(pdfContent, '_blank', 'noopener,noreferrer');
          // No fallback - only open in new window
        },
        error: () => {
          Swal.fire(
            this.translate.instant('COULD_NOT_GET_SDS'),
            this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
            'error'
          );
        }
      });
  }

  openSdsInNewWindow(product: { id: string | number; name: string }): void {
    // (Removed unused filename var to avoid TS noUnusedLocals errors)
    const tmpid = this.getProductId(product);
    this.productService.getSdsAsTrustedResourceUrl(tmpid);

    this.productService.getSdsDataUpdatedListener()
      .pipe(take(1))
      .subscribe({
        next: (pdfContent: string | SafeResourceUrl | null) => {
          if (!pdfContent) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_SDS'),
              this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
              'error'
            );
            return;
          }

          // Ensure we have a plain string URL
          const url =
            typeof pdfContent === 'string'
              ? pdfContent
              : this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, pdfContent) ?? null;

          if (!url) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_SDS'),
              this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
              'error'
            );
            return;
          }

          const newWindow = window.open(url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Fallback if popup blocked
            window.location.href = url;
          }
        },
        error: () => {
          Swal.fire(
            this.translate.instant('COULD_NOT_GET_SDS'),
            this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
            'error'
          );
        }
      });
  }

  openExtSdsInNewWindow(product: { id: string | number; name: string }): void {
    // (Removed unused filename var to avoid TS noUnusedLocals errors)
    const tmpid = this.getProductId(product);
    this.productService.getExtSdsAsTrustedResourceUrl(tmpid);

    this.productService.getExtSdsDataUpdatedListener()
      .pipe(take(1))
      .subscribe({
        next: (pdfContent: string | SafeResourceUrl | null) => {
          if (!pdfContent) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_SDS'),
              this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
              'error'
            );
            return;
          }

          // Ensure we have a plain string URL
          const url =
            typeof pdfContent === 'string'
              ? pdfContent
              : this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, pdfContent) ?? null;

          if (!url) {
            Swal.fire(
              this.translate.instant('COULD_NOT_GET_SDS'),
              this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
              'error'
            );
            return;
          }

          const newWindow = window.open(url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Fallback if popup blocked
            window.location.href = url;
          }
        },
        error: () => {
          Swal.fire(
            this.translate.instant('COULD_NOT_GET_SDS'),
            this.translate.instant('COULD_NOT_GET_SDS_WARNING'),
            'error'
          );
        }
      });
  }

  async getAllDepartments(organisation: Org): Promise<any[]> {
    if (!organisation) return this.listAllDepartments();

    // kick off the request (fire-and-forget trigger)
    this.organisationService.getAllDepartmentNames(organisation);

    // wait for the first emission (or [] on error)
    const departments = await firstValueFrom(
      this.organisationService
        .getOrganisationAllDepartmentNamesDataListener()
        .pipe(
          take(1),
          catchError(err => {
            console.error('Error:', err);
            return of<any[]>([]);
          })
        )
    );

    // sync internal signal and return the final value
    this.listAllDepartments.set(departments ?? []);
    return this.listAllDepartments();
  }

  /** True for 1 / '1' / true flag values from API / localStorage. */
  isTruthyFlag(value: unknown): boolean {
    return value === 1 || value === '1' || value === true;
  }

  /** Admin, local admin, or a higher role. These roles may set user_can_change_password. */
  canSetUserPasswordPolicy(user: any): boolean {
    if (!user) return false;
    return this.isTruthyFlag(user.is_local_admin)
      || this.isTruthyFlag(user.is_admin)
      || this.isTruthyFlag(user.is_useradmin)
      || this.isTruthyFlag(user.is_super_user)
      || this.isTruthyFlag(user.is_super_admin);
  }

  /**
   * Plain user or local user, using the same last-flag-wins order as the user role dropdown.
   * Higher roles are never treated as a plain user.
   */
  isUserOrLocalUser(user: any): boolean {
    if (!user) return false;
    if (this.isTruthyFlag(user.is_guest)) return false;
    if (this.isTruthyFlag(user.is_readonly_user)) return false;
    if (this.isTruthyFlag(user.is_local_user)) return true;
    if (this.isTruthyFlag(user.is_maintenance_user)) return false;
    if (this.isTruthyFlag(user.is_local_admin)) return false;
    if (this.isTruthyFlag(user.is_admin)) return false;
    if (this.isTruthyFlag(user.is_useradmin) || this.isTruthyFlag(user.is_super_user) || this.isTruthyFlag(user.is_super_admin)) {
      return false;
    }
    return true;
  }

  isGroupAdmin(user: any): boolean {
    return !!(user && this.isTruthyFlag(user.is_group_admin) && user.group_id);
  }

  /** localStorage: group admin has entered a company via Change organisation. */
  static readonly GROUP_ADMIN_COMPANY_MODE_KEY = 'groupAdminCompanyMode';

  isGroupAdminCompanyMode(): boolean {
    return localStorage.getItem(UserHelperService.GROUP_ADMIN_COMPANY_MODE_KEY) === 'true';
  }

  /** Koncern shell: group admin who has not entered a company yet. */
  isGroupAdminInKoncernMode(user: any): boolean {
    return this.isGroupAdmin(user) && !this.isGroupAdminCompanyMode();
  }

  enterGroupAdminCompanyMode(): void {
    localStorage.setItem(UserHelperService.GROUP_ADMIN_COMPANY_MODE_KEY, 'true');
  }

  exitGroupAdminCompanyMode(): void {
    localStorage.removeItem(UserHelperService.GROUP_ADMIN_COMPANY_MODE_KEY);
  }

  /**
   * Group admins act as company admins only after Change organisation (company mode).
   * Elevates is_admin in the session copy only (does not change backend role).
   */
  applyGroupAdminPortalRights<T extends Record<string, any>>(user: T | null | undefined): T | null | undefined {
    if (!user || !this.isTruthyFlag(user['is_group_admin']) || !this.isGroupAdminCompanyMode()) {
      return user;
    }
    return { ...user, is_admin: 1 };
  }
}
